import type { CourseTreeNode } from "@/types/generated";
import { ProgressRing } from "@/components/ui/progress-ring";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { stripThirdLevelNumbering } from "@/lib/courseUtils";
import type { AggregatedProgress } from "@/components/course/tree/types";
import {
  treeChromeClass,
  treeTitleClass,
} from "@/components/course/tree/treeStyles";
import { cn } from "@/lib/utils";

interface MiniModuleNodeProps {
  module: CourseTreeNode;
  moduleIndex: number;
  moduleProgress?: AggregatedProgress;
  isActive: boolean;
  activeLessonTitle?: string;
  onExpandSidebar?: () => void;
}

export const MiniModuleNode = ({
  module,
  moduleIndex,
  moduleProgress,
  isActive,
  activeLessonTitle,
  onExpandSidebar,
}: MiniModuleNodeProps) => {
  const expandModule = useCourseDetailStore((s) => s.expandModule);
  const progress = moduleProgress?.progressPercent ?? 0;
  const chapters = module.children?.filter((c) => c.type === "chapter") || [];
  const totalLessons = moduleProgress?.totalLessons ?? 0;

  const handleClick = () => {
    expandModule(module.id);
    onExpandSidebar?.();
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          onClick={handleClick}
          className={`
            relative w-full text-left px-3 py-2 cursor-pointer
            hover:bg-gray-50 transition-colors
            ${moduleIndex > 1 ? "border-t border-gray-200" : ""}
          `}
        >
          {/* Blue left-edge bar for active module */}
          {isActive && (
            <div className="absolute left-0 top-1 bottom-1 w-[3px] rounded-r bg-primary/80" />
          )}

          <div className="flex items-center gap-2">
            <ProgressRing
              percentage={progress}
              size="xs"
              fillColor={
                progress === 100 ? "var(--success)" : "var(--secondary)"
              }
            />
            <div
              className={cn(
                "text-xs font-bold truncate",
                treeTitleClass(isActive)
              )}
            >
              {moduleIndex}. {module.title}
            </div>
          </div>

          {/* Active lesson title */}
          {isActive && activeLessonTitle && (
            <div
              className={cn(
                "mt-0.5 pl-7 text-[11px] truncate",
                treeChromeClass
              )}
            >
              {" "}
              {stripThirdLevelNumbering(activeLessonTitle)}
            </div>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-[260px]">
        <div className="font-semibold">
          {moduleIndex}. {module.title}
        </div>
        <div className="text-xs opacity-80 mt-0.5">
          {chapters.length} Kap. · {totalLessons} Lekt.
        </div>
        {isActive && activeLessonTitle && (
          <div className="text-xs opacity-80 mt-0.5">
            Aktuelle Lektion: {stripThirdLevelNumbering(activeLessonTitle)}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
};
